import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, tokenStore, probeCookieAuth, errMsg } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = unauth, object = auth
  const [profile, setProfile] = useState(null);

  // Fetches the current session. The axios interceptor will transparently attempt a
  // refresh (cookie on first-party, localStorage fallback in the preview iframe) on 401.
  const loadMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setProfile(data.organizer_profile || null);
      return data.user;
    } catch (e) {
      tokenStore.clear();
      setUser(false);
      setProfile(null);
      return null;
    }
  }, []);

  // On boot, try to restore an existing session (cookie in prod / fallback refresh in preview).
  useEffect(() => {
    loadMe();
  }, [loadMe]);

  // After login/register we detect whether httpOnly cookies work here. If they don't
  // (3rd-party iframe preview), we persist a refresh token so reloads survive. On a
  // first-party domain nothing is written to localStorage — the cookie is authoritative.
  const establishSession = async (data) => {
    tokenStore.setTokens(data.access_token, data.refresh_token);
    const cookieWorks = await probeCookieAuth();
    if (!cookieWorks) tokenStore.persistFallback();
    setUser(data.user);
    await loadMe();
    return data.user;
  };

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    return establishSession(data);
  };

  const register = async (name, email, password, extra = {}) => {
    const { data } = await api.post("/auth/register", { name, email, password, ...extra });
    return establishSession(data);
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // ignore
    }
    tokenStore.clear();
    setUser(false);
    setProfile(null);
  };

  const refreshProfile = loadMe;

  return (
    <AuthContext.Provider
      value={{ user, profile, login, register, logout, refreshProfile, isAdmin: user?.role === "admin" || user?.role === "superadmin", isSuperAdmin: user?.role === "superadmin", errMsg }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
